import { createContext, useContext } from 'react';
import { ApiClient } from '../services/apiClient';
import { CargasApi } from '../services/cargasApi';
import { BasesApi } from '../services/basesApi';
import { ConsultaApi } from '../services/consultaApi';
import { EdicaoApi } from '../services/edicaoApi';
import { ExportacoesApi } from '../services/exportacoesApi';
import { LotesApi } from '../services/lotesApi';
import { PainelApi } from '../services/painelApi';
import { UsuariosApi } from '../services/usuariosApi';
import { AuditoriaApi } from '../services/auditoriaApi';
import { MeApi } from '../services/meApi';
import { PlanilhasApi } from '../services/planilhasApi';

/** Serviços de API disponibilizados à árvore de componentes. */
export interface Services {
  readonly apiBaseUrl: string;
  readonly cargas: CargasApi;
  readonly bases: BasesApi;
  readonly consulta: ConsultaApi;
  readonly edicao: EdicaoApi;
  readonly exportacoes: ExportacoesApi;
  readonly lotes: LotesApi;
  readonly painel: PainelApi;
  readonly usuarios: UsuariosApi;
  readonly auditoria: AuditoriaApi;
  readonly me: MeApi;
  /** ADR-038: carga da planilha de instalações sem termo (admin). */
  readonly planilhas: PlanilhasApi;
}

/**
 * Contexto de serviços. O provider concreto (com ApiClient real ligado ao token
 * do AuthContext) é montado em `App`. O default lança se usado sem provider —
 * evita chamadas silenciosas contra uma base inexistente.
 */
export const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const ctx = useContext(ServicesContext);
  if (!ctx) {
    throw new Error('useServices deve ser usado dentro de um ServicesProvider.');
  }
  return ctx;
}

/** Constrói os serviços a partir da URL base, provedor de token e handler de 401. */
export function buildServices(
  apiBaseUrl: string,
  getToken: () => string | undefined,
  onUnauthorized?: () => void,
): Services {
  const client = new ApiClient({ baseUrl: apiBaseUrl, getToken, onUnauthorized });
  return {
    apiBaseUrl,
    cargas: new CargasApi(client),
    bases: new BasesApi(client),
    consulta: new ConsultaApi(client),
    edicao: new EdicaoApi(client),
    exportacoes: new ExportacoesApi(client),
    lotes: new LotesApi(client),
    painel: new PainelApi(client),
    usuarios: new UsuariosApi(client),
    auditoria: new AuditoriaApi(client),
    me: new MeApi(client),
    planilhas: new PlanilhasApi(client),
  };
}
